
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Palette, Check, X } from 'lucide-react';
import { ExtractedColors } from '@/utils/colorExtractor';

interface ColorSuggestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  extractedColors: ExtractedColors | null;
  onApply: (colors: ExtractedColors) => void;
  onDiscard: () => void;
  loading?: boolean;
}

export const ColorSuggestionDialog: React.FC<ColorSuggestionDialogProps> = ({
  isOpen,
  onClose,
  extractedColors,
  onApply,
  onDiscard,
  loading = false
}) => {
  if (!extractedColors) return null;

  const handleApply = () => {
    onApply(extractedColors);
    onClose();
  };

  const handleDiscard = () => {
    onDiscard();
    onClose();
  };

  const colorPresets = [
    { key: 'primary', label: 'Primary Color', color: extractedColors.primary },
    { key: 'secondary', label: 'Secondary Color', color: extractedColors.secondary },
    { key: 'accent', label: 'Accent Color', color: extractedColors.accent },
    { key: 'background', label: 'Background Color', color: extractedColors.background },
    { key: 'text', label: 'Text Color', color: extractedColors.text }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Suggested Colors from Logo
          </DialogTitle>
          <DialogDescription>
            We've extracted colors from your logo. Would you like to apply this color scheme to your menu?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-3">
                {colorPresets.map(({ key, label, color }) => (
                  <div key={key} className="flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-full border-2 border-gray-200"
                      style={{ backgroundColor: color }}
                    />
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-gray-500">{color}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={handleDiscard} disabled={loading}>
              <X className="h-4 w-4 mr-2" />
              Discard
            </Button>
            <Button onClick={handleApply} disabled={loading}>
              <Check className="h-4 w-4 mr-2" />
              Apply Colors
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
